// Flow App JavaScript for Shopify Theme App Extension - Updated Design
(() => {
  'use strict';
  
  // Global state for the flow journey
  let flowState = {
    currentStep: 1,
    totalSteps: 5,
    selectedSubflow: null,
    selectedSize: null,
    selectedVariants: {},
    selectedAddOns: {},
    flowConfig: null,
    currency: 'GBP',
    moneyFormat: '£{{amount}}',
    lockedCurrency: null,
    pricing: {
      basePattern: 0,
      yarns: 0,
      addons: 0,
      total: 0
    }
  };

  // Helper function to format money with currency
  function formatMoney(amount, currencyCode = null, format = null) {
    const curr = currencyCode || flowState.currency || 'GBP';
    const fmt = format || flowState.moneyFormat || '£{{amount}}';
    
    // Determine which placeholder is used and format accordingly
    let formattedAmount;
    let placeholder;
    
    if (fmt.includes('{{amount_with_comma_separator}}')) {
      // Use comma as decimal separator (e.g., 10,00)
      formattedAmount = amount.toFixed(2).replace('.', ',');
      placeholder = '{{amount_with_comma_separator}}';
    } else if (fmt.includes('{{amount_with_space_separator}}')) {
      // Use space for thousands and comma for decimals (e.g., 1 000,00)
      formattedAmount = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      placeholder = '{{amount_with_space_separator}}';
    } else if (fmt.includes('{{amount_no_decimals}}')) {
      // No decimal places (e.g., 10)
      formattedAmount = Math.round(amount).toString();
      placeholder = '{{amount_no_decimals}}';
    } else if (fmt.includes('{{amount_no_decimals_with_comma_separator}}')) {
      // No decimals with comma grouping (e.g., 1.000)
      formattedAmount = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      placeholder = '{{amount_no_decimals_with_comma_separator}}';
    } else {
      // Default: {{amount}} with period as decimal separator (e.g., 10.00)
      formattedAmount = amount.toFixed(2);
      placeholder = '{{amount}}';
    }
    
    // Replace the placeholder with formatted number
    return fmt.replace(placeholder, formattedAmount);
  }

  // Wait for DOM to be ready
  document.addEventListener('DOMContentLoaded', () => {
    initFlowApp();
  });

  async function initFlowApp() {
    const container = document.getElementById('flow-app-container');
    if (!container) {
      console.log('DEBUG: flow-app-container not found');
      return;
    }

    console.log('DEBUG: Container found, starting initialization');

    const productId = container.dataset.productId;
    const productHandle = container.dataset.productHandle;
    const apiUrl = container.dataset.apiUrl || 'https://hparptkwdymlqycbddyc.supabase.co/functions/v1/get-flow-config';
    const currency = container.dataset.currency || 'GBP';
    const moneyFormat = container.dataset.moneyFormat || '£{{amount}}';

    // Store currency in flowState
    flowState.currency = currency;
    flowState.moneyFormat = moneyFormat;

    console.log('DEBUG: Product data -', { productId, productHandle, apiUrl, currency, moneyFormat });

    try {
      // Show loading state
      container.innerHTML = '<div class="flow-app-loading">Loading product options...</div>';

      // Fetch flow configuration from API
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhwYXJwdGt3ZHltbHF5Y2JkZHljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI4ODg1ODQsImV4cCI6MjA1ODQ2NDU4NH0.XCOvSdTTakj_snmbCVPpFJBlo9YOGtFZMPNbcSK3ORw'
      };

      const response = await fetch(`${apiUrl}?product_id=${productId || ''}&handle=${productHandle || ''}&currency=${currency}`, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load product flow configuration');
      }

      const flowConfig = await response.json();
      console.log('DEBUG: Flow config received:', flowConfig);
      
      // Store the flow config globally
      flowState.flowConfig = flowConfig;
      
      // Validate flow configuration
      if (!flowConfig || !flowConfig.subflows || flowConfig.subflows.length === 0) {
        console.log('DEBUG: No flow config or subflows found, hiding container');
        container.style.display = 'none';
        return;
      }

      // Check if any subflow has sizes
      const hasAnySizes = flowConfig.subflows.some(subflow => subflow.sizes && subflow.sizes.length > 0);
      
      if (!hasAnySizes) {
        console.log('DEBUG: No sizes found in any subflow, hiding container');
        container.style.display = 'none';
        return;
      }

      console.log('DEBUG: All validation passed, showing activation button');
      // Show the activation button first
      renderActivationButton(container);
      keepPlaced(); // moves the block under the buy button and keeps it there
      enforceFlowOnlyIfNeeded(); // prevent default add to cart if flow is required
      
      // Set default selections after loading config
      setDefaultSelections();
    } catch (error) {
      console.error('Error initializing flow app:', error);
      container.innerHTML = '<div class="flow-app-error">Unable to load product options. Please refresh the page.</div>';
    }
  }

  function setDefaultSelections() {
    const { flowConfig } = flowState;
    
    if (!flowConfig) return;
    
    console.log('DEBUG: Setting default selections', {
      defaultSubflowId: flowConfig.defaultSubflowId,
      defaultSizeId: flowConfig.defaultSizeId
    });
    
    // Set default subflow if specified
    if (flowConfig.defaultSubflowId) {
      const defaultSubflow = flowConfig.subflows.find(s => s.id === flowConfig.defaultSubflowId);
      if (defaultSubflow) {
        flowState.selectedSubflow = defaultSubflow.id;
        flowState.pricing.basePattern = parseFloat(defaultSubflow.basePrice || 0);
        console.log('DEBUG: Set default subflow:', defaultSubflow.id, 'price:', flowState.pricing.basePattern);
        
        // Set default size if specified and exists in the selected subflow
        if (flowConfig.defaultSizeId && defaultSubflow.sizes) {
          const defaultSize = defaultSubflow.sizes.find(s => s.id === flowConfig.defaultSizeId);
          if (defaultSize) {
            flowState.selectedSize = defaultSize.id;
            console.log('DEBUG: Set default size:', defaultSize.id);
            
            // Calculate yarn pricing for the default size
            calculateYarnPricing();
          } else {
            console.log('DEBUG: Default size not found:', flowConfig.defaultSizeId);
          }
        }
      } else {
        console.log('DEBUG: Default subflow not found:', flowConfig.defaultSubflowId);
      }
    }
    
    // Update pricing after setting defaults
    updateCartButtonPrice();
  }

  function enforceFlowOnlyIfNeeded() {
    if (!window.__requiresFlow) return;

    // Safety net - if some theme tries to submit the form anyway
    const atc =
      document.querySelector('product-form button[name="add"]') ||
      document.querySelector('form[action^="/cart/add"] button[name="add"]') ||
      document.querySelector('button[name="add"]');
    const form = atc ? atc.closest('form') : document.querySelector('form[action^="/cart/add"]');
    if (form && !form.dataset.flowGuarded) {
      form.dataset.flowGuarded = '1';
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();
        document.getElementById('flow-app-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, true);
    }
  }

  function renderActivationButton(container) {
    console.log('DEBUG: Rendering activation button');
    
    container.innerHTML = '';
    container.classList.add('flow-app');
    container.classList.remove('flow-app-active');

    const activationContainer = document.createElement('div');
    activationContainer.className = 'flow-app-activation';

    const toggleButton = document.createElement('button');
    toggleButton.className = 'flow-app-activation-button';
    
    const buttonContent = document.createElement('div');
    buttonContent.className = 'flow-app-activation-content';
    
    const yarnIcon = document.createElement('div');
    yarnIcon.className = 'flow-app-yarn-icon';
    yarnIcon.innerHTML = `<svg width="28" height="28" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M44.6647 26.2694L60.0174 15.374C60.006 15.2509 60.0002 15.1261 60.0002 15C60.0002 12.7909 61.791 11 64.0002 11C66.2093 11 68.0002 12.7909 68.0002 15C68.0002 17.2091 66.2093 19 64.0002 19C63.4047 19 62.8396 18.8699 62.3318 18.6365L46.9525 29.5508C48.8913 32.9223 50.0002 36.8317 50.0002 41C50.0002 53.7025 39.7027 64 27.0002 64C22.6808 64 18.6396 62.8093 15.1867 60.7383L10.6312 67.1575C9.99195 68.0583 8.74348 68.2703 7.8427 67.631C6.94192 66.9917 6.72986 65.7433 7.36913 64.8425L11.9477 58.3908C11.1136 57.6682 10.3319 56.8866 9.6093 56.0524L3.15764 60.631C2.25686 61.2703 1.0084 61.0583 0.369129 60.1575C-0.270138 59.2567 -0.0580831 58.0083 0.842701 57.369L7.26183 52.8135C5.19083 49.3606 4.00017 45.3193 4.00017 41C4.00017 28.2975 14.2976 18 27.0002 18C31.1685 18 35.0778 19.1088 38.4493 21.0477L49.3637 5.66843C49.1303 5.1606 49.0002 4.59549 49.0002 4C49.0002 1.79086 50.791 0 53.0002 0C55.2093 0 57.0002 1.79086 57.0002 4C57.0002 6.20914 55.2093 8 53.0002 8C52.8741 8 52.7493 7.99417 52.6261 7.98276L41.7307 23.3354C42.7944 24.2234 43.7767 25.2057 44.6647 26.2694ZM34.9901 58.2434C34.79 50.74 31.6206 43.7828 26.3235 38.7349C26.1098 40.1325 26.0002 41.5574 26.0002 43C26.0002 49.0226 27.9052 54.7492 31.3717 59.4947C32.6308 59.1982 33.8415 58.7765 34.9901 58.2434ZM38.8469 55.8554C39.5909 55.2613 40.2892 54.6126 40.9358 53.9152C40.9786 53.28 41.0002 52.6414 41.0002 52C41.0002 42.929 36.6539 34.6065 29.5223 29.391C28.6495 30.958 27.9287 32.608 27.3714 34.3205C33.8688 39.6863 38.0182 47.39 38.8469 55.8554ZM44.7311 47.8416C45.5508 45.7188 46.0002 43.4119 46.0002 41C46.0002 32.9982 41.0536 26.1517 34.0516 23.3515C33.2136 24.2017 32.4342 25.1002 31.7167 26.041C38.8537 31.1908 43.5868 39.0541 44.7311 47.8416ZM29.69 22.1889C28.8113 22.0644 27.9133 22 27.0002 22C26.8232 22 26.6468 22.0024 26.4711 22.0072C20.4965 27.2831 17.0002 34.8546 17.0002 43C17.0002 48.987 18.8821 54.6833 22.3162 59.4183C23.7785 59.789 25.3084 59.9902 26.8838 59.9997C23.7209 54.9661 22.0002 49.1127 22.0002 43C22.0002 35.2363 24.7813 27.9146 29.69 22.1889ZM15.9637 56.4678C14.0301 52.3052 13.0002 47.7296 13.0002 43C13.0002 35.7982 15.3949 28.9842 19.641 23.4778C12.8022 26.3534 8.00017 33.1159 8.00017 41C8.00017 47.3778 11.1426 53.0217 15.9637 56.4678Z" fill="white"/></svg>`;
    // Center the icon and text in the button
    buttonContent.style.display = 'flex';
    buttonContent.style.alignItems = 'center';
    buttonContent.style.justifyContent = 'center';
    buttonContent.style.gap = '12px';
    
    const buttonText = document.createElement('span');
    buttonText.className = 'flow-app-activation-text';
    buttonText.textContent = 'Buy yarn for this pattern';
    
    buttonContent.appendChild(yarnIcon);
    buttonContent.appendChild(buttonText);
    toggleButton.appendChild(buttonContent);

    toggleButton.addEventListener('click', () => {
      renderAllInOne(container);
    });

    activationContainer.appendChild(toggleButton);
    container.appendChild(activationContainer);
    
    console.log('DEBUG: Activation button rendered successfully');
  }

  function renderAllInOne(container) {
    container.innerHTML = '';
    container.classList.add('flow-app', 'flow-app-active');
    
    // Lock currency when flow is activated
    flowState.lockedCurrency = flowState.currency;

    // Check if addons exist
    const hasAddOns = flowState.flowConfig.addOns && 
                      flowState.flowConfig.addOns.length > 0 &&
                      flowState.flowConfig.addOns.some(addOn => addOn.available !== false);

    // Section containers
    const recommendationSection = document.createElement('div');
    const sizeSection = document.createElement('div');
    const yarnSection = document.createElement('div');
    const addOnSection = hasAddOns ? document.createElement('div') : null;
    const reviewSection = document.createElement('div');

    container.appendChild(recommendationSection);
    container.appendChild(sizeSection);
    container.appendChild(yarnSection);
    if (addOnSection) {
      container.appendChild(addOnSection);
    }
    container.appendChild(reviewSection);

    // Pass section containers to render functions
    const allInOneSections = { sizeSection, yarnSection, addOnSection, reviewSection };
    renderRecommendationSelection(recommendationSection, true, allInOneSections);
    renderSizeSelection(sizeSection, true, allInOneSections);
    renderYarnSelection(yarnSection, true);
    if (addOnSection) {
      renderAddOns(addOnSection, true, allInOneSections);
    }
    renderOrderReview(reviewSection, true);
  }

  function renderRecommendationSelection(container, isAllInOne = false, allInOneSections = null) {
    console.log('DEBUG: Rendering recommendation selection');
    
    container.innerHTML = '';
    container.classList.add('flow-app-step-content');

    const heading = document.createElement('h3');
    heading.className = 'flow-app-heading';
    heading.textContent = 'Select your yarn';
    container.appendChild(heading);

    const description = document.createElement('p');
    description.className = 'flow-app-description';
    description.textContent = 'Start by selecting one of our curated yarn recommendations';
    container.appendChild(description);

    const subflowOptions = document.createElement('div');
    subflowOptions.className = 'flow-app-flow-options';

    flowState.flowConfig.subflows.forEach((subflow, index) => {
      const option = document.createElement('div');
      option.className = `flow-app-flow-option ${subflow.id === flowState.selectedSubflow ? 'selected' : ''}`;
      
      const input = document.createElement('input');
      input.type = 'radio';
      input.id = `subflow-${subflow.id || index}`;
      input.name = 'subflow-selection';
      input.value = subflow.id || index;
      input.checked = subflow.id === flowState.selectedSubflow;
      
      const content = document.createElement('div');
      content.className = 'flow-app-flow-content';
      
      const title = document.createElement('div');
      title.className = 'flow-app-flow-title';
      title.textContent = subflow.name;
      title.title = subflow.name;
      content.appendChild(title);

      if (subflow.description) {
        const desc = document.createElement('div');
        desc.className = 'flow-app-flow-description';
        desc.textContent = subflow.description;
        desc.title = subflow.description;
        content.appendChild(desc);
      }

      function handleRecommendationChange() {
        flowState.selectedSubflow = subflow.id;
        flowState.pricing.basePattern = parseFloat(subflow.basePrice || 0);
        document.querySelectorAll('.flow-app-flow-option').forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
        updateNavigationButtonState();
        updateCartButtonPrice();
        if (isAllInOne && allInOneSections) {
          // Reset yarn selections
          flowState.selectedVariants = {};

          // Auto-select a default size for the new subflow
          const newSubflow = flowState.flowConfig.subflows.find(s => s.id === flowState.selectedSubflow);
          if (newSubflow && Array.isArray(newSubflow.sizes) && newSubflow.sizes.length > 0) {
            const defaultSize =
              (flowState.flowConfig.defaultSizeId &&
               newSubflow.sizes.find(s => s.id === flowState.flowConfig.defaultSizeId)) ||
              newSubflow.sizes[0];
            flowState.selectedSize = defaultSize?.id ?? null;
          } else {
            flowState.selectedSize = null;
          }

          // Re-render dependent sections
          if (flowState.selectedSubflow) {
            renderSizeSelection(allInOneSections.sizeSection, true, allInOneSections);
            renderYarnSelection(allInOneSections.yarnSection, true);
            if (allInOneSections.addOnSection) {
              renderAddOns(allInOneSections.addOnSection, true);
            }
            renderOrderReview(allInOneSections.reviewSection, true);
          } else {
            console.error('DEBUG: Cannot re-render sections - no valid subflow selected after change');
          }
        }
      }

      option.addEventListener('click', (e) => {
        if (e.target !== input) {
          input.checked = true;
          handleRecommendationChange();
        }
      });
      input.addEventListener('change', () => {
        if (input.checked) {
          handleRecommendationChange();
        }
      });

      option.appendChild(input);
      option.appendChild(content);
      subflowOptions.appendChild(option);
    });

    container.appendChild(subflowOptions);

    if (!isAllInOne) {
      // Navigation
      const navigation = createNavigation(true, flowState.selectedSubflow !== null, flowState.pricing.basePattern > 0 ? formatMoney(flowState.pricing.basePattern) : null);
      container.appendChild(navigation);
    }
  }

  function renderSizeSelection(container, isAllInOne = false, allInOneSections = null) {
    console.log('DEBUG: Rendering size selection');
    
    container.innerHTML = '';
    container.classList.add('flow-app-step-content');

    const heading = document.createElement('h3');
    heading.className = 'flow-app-heading';
    heading.textContent = 'Select your size';
    container.appendChild(heading);

    const description = document.createElement('p');
    description.className = 'flow-app-description';
    description.textContent = 'Find our size guide below';
    container.appendChild(description);

    const selectedSubflow = flowState.flowConfig.subflows.find(sf => 
      sf.id === flowState.selectedSubflow ||
      (typeof flowState.selectedSubflow === 'number' && flowState.flowConfig.subflows.indexOf(sf) === flowState.selectedSubflow)
    );
    if (!selectedSubflow) {
      console.error('DEBUG: Selected subflow not found in renderSizeSelection', {
        selectedSubflowId: flowState.selectedSubflow,
        availableSubflows: flowState.flowConfig.subflows.map(sf => ({ id: sf.id, name: sf.name }))
      });
      return;
    }

    const sizeOptions = document.createElement('div');
    sizeOptions.className = 'flow-app-size-options';

    selectedSubflow.sizes.forEach((size, index) => {
      const sizeOption = document.createElement('div');
      sizeOption.className = 'flow-app-size-option';

      const input = document.createElement('input');
      input.type = 'radio';
      input.id = `flow-size-${size.id || index}`;
      input.name = 'flow-size';
      input.value = size.id || index;
      input.checked = (size.id || index) === flowState.selectedSize;

      const label = document.createElement('label');
      label.htmlFor = `flow-size-${size.id || index}`;
      label.textContent = size.name;
      label.title = size.name;

      input.addEventListener('change', () => {
        if (input.checked) {
          flowState.selectedSize = size.id || index;
          // Reset selected variants when size changes
          flowState.selectedVariants = {};
          updateNavigationButtonState();
          // --- NEW: In all-in-one mode, re-render dependent sections ---
          if (isAllInOne && allInOneSections) {
            renderYarnSelection(allInOneSections.yarnSection, true);
            if (allInOneSections.addOnSection) {
              renderAddOns(allInOneSections.addOnSection, true);
            }
            renderOrderReview(allInOneSections.reviewSection, true);
          }
        }
      });

      sizeOption.appendChild(input);
      sizeOption.appendChild(label);
      sizeOptions.appendChild(sizeOption);
    });

    container.appendChild(sizeOptions);

    if (!isAllInOne) {
      // Navigation
      const navigation = createNavigation(true, flowState.selectedSize !== null, formatMoney(flowState.pricing.basePattern));
      container.appendChild(navigation);
    }
  }

  function renderYarnSelection(container, isAllInOne = false) {
    console.log('DEBUG: Rendering yarn selection');
    
    container.innerHTML = '';
    container.classList.add('flow-app-step-content');

    const heading = document.createElement('h3');
    heading.className = 'flow-app-heading';
    heading.textContent = 'Select your colour';
    container.appendChild(heading);

    const description = document.createElement('p');
    description.className = 'flow-app-description';
    description.textContent = 'Select the yarn colour for your pattern';
    container.appendChild(description);

    const selectedSubflow = flowState.flowConfig.subflows.find(sf => 
      sf.id === flowState.selectedSubflow ||
      (typeof flowState.selectedSubflow === 'number' && flowState.flowConfig.subflows.indexOf(sf) === flowState.selectedSubflow)
    );
    const selectedSize = selectedSubflow?.sizes.find(size => (size.id || selectedSubflow.sizes.indexOf(size)) === flowState.selectedSize);
    
    if (!selectedSubflow) {
      console.error('DEBUG: Selected subflow not found in renderYarnSelection', {
        selectedSubflowId: flowState.selectedSubflow,
        availableSubflows: flowState.flowConfig.subflows.map(sf => ({ id: sf.id, name: sf.name }))
      });
      return;
    }

    if (!selectedSize) {
      console.error('DEBUG: Selected size not found in renderYarnSelection', {
        selectedSizeId: flowState.selectedSize,
        selectedSubflowId: flowState.selectedSubflow,
        availableSizes: selectedSubflow?.sizes?.map(size => ({ id: size.id, name: size.name })) || []
      });
      return;
    }

    const productsSection = document.createElement('div');
    productsSection.className = 'flow-app-products-list';

    const productsGrid = document.createElement('div');
    productsGrid.className = 'flow-app-products-grid';

    // Calculate yarn prices
    let yarnTotal = 0;
    selectedSize.products.forEach((product, index) => {
      // Skip unavailable products
      if (product.available === false) return;
      
      const productCard = createYarnProductCard(product, index);
      productsGrid.appendChild(productCard);
      
      // Add to yarn total
      if (product.price) {
        yarnTotal += parseFloat(product.price) * product.quantity;
      }
    });

    flowState.pricing.yarns = yarnTotal;

    productsSection.appendChild(productsGrid);
    container.appendChild(productsSection);

    if (!isAllInOne) {
      // Navigation - Continue button shows total price
      const totalPrice = flowState.pricing.basePattern + flowState.pricing.yarns;
      const navigation = createNavigation(true, true, formatMoney(totalPrice));
      container.appendChild(navigation);
    }
  }

  function createYarnProductCard(product, index) {
    const productCard = document.createElement('div');
    productCard.className = 'flow-app-product-card';
    productCard.dataset.productId = product.productId;

    // Product image
    const imageContainer = document.createElement('div');
    imageContainer.className = 'flow-app-product-image';
    
    const img = document.createElement('img');
    img.src = product.imageUrl || 'https://via.placeholder.com/120x120?text=Yarn';
    img.alt = product.productTitle;
    imageContainer.appendChild(img);

    // Product details
    const detailsContainer = document.createElement('div');
    detailsContainer.className = 'flow-app-product-details';

    const header = document.createElement('div');
    header.className = 'flow-app-product-header';

    const titleContainer = document.createElement('div');
    
    const title = document.createElement('div');
    title.className = 'flow-app-product-title';
    title.textContent = product.productTitle;
    title.title = product.productTitle;
    titleContainer.appendChild(title);

    // Show selected variant name and quantity together
    let currentVariantTitle = product.variantTitle || '';
    if (product.isParentProduct && flowState.selectedVariants[product.productId]) {
      const selectedVariant = product.variants?.find(v => v.variantId === flowState.selectedVariants[product.productId]);
      if (selectedVariant) {
        currentVariantTitle = selectedVariant.variantTitle;
      }
    }
    const variantAndQty = document.createElement('div');
    variantAndQty.className = 'flow-app-product-variant-name';
    variantAndQty.textContent = `${currentVariantTitle} • ${product.quantity}pcs`;
    variantAndQty.title = `${currentVariantTitle} • ${product.quantity}pcs`;
    titleContainer.appendChild(variantAndQty);

    header.appendChild(titleContainer);

    // Always add chevron to the right
    const chevron = document.createElement('div');
    chevron.className = 'flow-app-expand-chevron';
    header.appendChild(chevron);

    detailsContainer.appendChild(header);

    const info = document.createElement('div');
    info.className = 'flow-app-product-info';
    
    // Always show price, using selected variant price if available
    const price = document.createElement('span');
    price.className = 'flow-app-product-price';
    // Function to update main product image based on selected variant
    const updateMainProductImage = () => {
      if (product.isParentProduct) {
        const selectedVariantId = flowState.selectedVariants[product.productId];
        if (selectedVariantId && product.variants) {
          const selectedVariant = product.variants.find(v => v.variantId === selectedVariantId);
          if (selectedVariant && selectedVariant.imageUrl) {
            img.src = selectedVariant.imageUrl;
            img.alt = selectedVariant.variantTitle;
          }
        }
      }
    };

    const updatePriceDisplay = () => {
      let displayPrice = 0;
      if (product.isParentProduct) {
        const selectedVariantId = flowState.selectedVariants[product.productId];
        if (selectedVariantId && product.variants) {
          const selectedVariant = product.variants.find(v => v.variantId === selectedVariantId);
          if (selectedVariant) {
            if (selectedVariant.available === false) {
              price.textContent = 'Sold Out';
              price.classList.add('sold-out');
              return;
            }
            if (typeof selectedVariant.price === 'number') {
              displayPrice = selectedVariant.price * product.quantity;
            }
          }
        }
      } else {
        if (typeof product.price === 'number') {
          displayPrice = product.price * product.quantity;
        }
      }
      if (displayPrice > 0) {
        price.textContent = formatMoney(displayPrice);
        price.classList.remove('sold-out');
      } else {
        price.textContent = product.isParentProduct ? 'Select variant' : formatMoney(0);
        price.classList.remove('sold-out');
      }
    };
    updatePriceDisplay();
    info.appendChild(price);

    detailsContainer.appendChild(info);

    // Create a row for image and details
    const productRow = document.createElement('div');
    productRow.className = 'flow-app-product-row';
    productRow.appendChild(imageContainer);
    productRow.appendChild(detailsContainer);
    productCard.appendChild(productRow);

    // Variant grid as sibling below the row
    if (product.isParentProduct && product.variants && product.variants.length > 1) {
      let variantGrid = createVariantGrid(product, variantAndQty);
      variantGrid.classList.add('hidden');
      productCard.appendChild(variantGrid);

      const toggleGrid = () => {
        variantGrid.classList.toggle('hidden');
        if (!variantGrid.classList.contains('hidden')) {
          chevron.classList.add('expanded');
        } else {
          chevron.classList.remove('expanded');
        }
      };
      productCard.addEventListener('click', toggleGrid);
      chevron.addEventListener('click', (e) => { e.stopPropagation(); toggleGrid(); });

      // Auto-select default variant if none selected - prioritize admin-defined defaultVariantId
      if (!flowState.selectedVariants[product.productId]) {
        console.log('DEBUG: Auto-selecting variant for product:', product.productId, 'defaultVariantId:', product.defaultVariantId);
        console.log('DEBUG: Available variants:', product.variants?.map(v => ({ id: v.variantId, title: v.variantTitle, available: v.available })));
        
        // Use defaultVariantId if available, otherwise use first available variant
        const defaultVariant = product.defaultVariantId 
          ? product.variants.find(v => v.variantId === product.defaultVariantId)
          : product.variants.find(v => v.available !== false);
          
        console.log('DEBUG: Found default variant:', defaultVariant);
        if (defaultVariant) {
          flowState.selectedVariants[product.productId] = defaultVariant.variantId;
          variantAndQty.textContent = `${defaultVariant.variantTitle} • ${product.quantity}pcs`;
          updatePriceDisplay();
          updateMainProductImage();
          calculateYarnPricing();
          console.log('DEBUG: Selected variant:', defaultVariant.variantId, 'for product:', product.productId);
        }
      } else {
        console.log('DEBUG: Variant already selected for product:', product.productId, 'selected:', flowState.selectedVariants[product.productId]);
      }

      // Enhance variant selection to update price and totals
      variantGrid.querySelectorAll('.flow-app-variant-option').forEach(option => {
        option.addEventListener('click', () => {
          updatePriceDisplay();
          calculateYarnPricing();
        });
      });

      // In createYarnProductCard, after setting variantAndQty.textContent for parent products:
      if (product.isParentProduct && product.variants && product.variants.length > 0) {
        const allSoldOut = product.variants.every(v => v.available === false);
        if (allSoldOut && !flowState.selectedVariants[product.productId]) {
          variantAndQty.textContent = 'Sold Out';
          variantAndQty.classList.add('sold-out');
        }
      }
    }

    return productCard;
  }

  function createVariantGrid(product, variantNameElement) {
    const variantGrid = document.createElement('div');
    variantGrid.className = 'flow-app-variant-grid';

    product.variants.forEach((variant, index) => {
      const variantOption = document.createElement('div');
      variantOption.className = 'flow-app-variant-option';
      // Sold out indication
      if (variant.available === false) {
        variantOption.classList.add('sold-out');
        const soldOutLabel = document.createElement('div');
        soldOutLabel.className = 'flow-app-variant-soldout';
        soldOutLabel.textContent = 'Sold Out';
        variantOption.appendChild(soldOutLabel);
        variantOption.style.pointerEvents = 'none';
        variantOption.style.opacity = '0.5';
      }
      
      // Check if this variant is currently selected
      if (flowState.selectedVariants[product.productId] === variant.variantId) {
        variantOption.classList.add('selected');
        console.log('DEBUG: Marking variant as selected in grid:', variant.variantId);
      }
      
      const variantImage = document.createElement('div');
      variantImage.className = 'flow-app-variant-image';
      
      const img = document.createElement('img');
      img.src = variant.imageUrl || 'https://via.placeholder.com/60x60?text=Variant';
      img.alt = variant.variantTitle;
      variantImage.appendChild(img);
      
      const label = document.createElement('div');
      label.className = 'flow-app-variant-label';
      label.textContent = variant.variantTitle || `Variant ${index + 1}`;

      variantOption.addEventListener('click', () => {
        // Update selected variant
        flowState.selectedVariants[product.productId] = variant.variantId;
        
        // Update UI
        variantGrid.querySelectorAll('.flow-app-variant-option').forEach(opt => opt.classList.remove('selected'));
        variantOption.classList.add('selected');
        
        // Update parent card display
        variantNameElement.textContent = `${variant.variantTitle} • ${product.quantity}pcs`;
        
        // Update main product image
        const mainProductImage = variantOption.closest('.flow-app-product-card')?.querySelector('.flow-app-product-image img');
        if (mainProductImage && variant.imageUrl) {
          mainProductImage.src = variant.imageUrl;
          mainProductImage.alt = variant.variantTitle;
        }
        
        // Close the variant grid
        variantGrid.classList.add('hidden');
        const chevron = variantGrid.closest('.flow-app-product-details')?.querySelector('.flow-app-expand-chevron');
        if (chevron) {
          chevron.classList.remove('expanded');
        }
        
        // Update price if variant has different price
        if (variant.price !== undefined) {
          const priceElement = variantOption.closest('.flow-app-product-card')?.querySelector('.flow-app-product-price');
          if (priceElement) {
            const totalPrice = product.quantity * parseFloat(variant.price);
            priceElement.textContent = formatMoney(totalPrice);
          }
          // Recalculate total pricing
          calculateYarnPricing();
        }
      });

      variantOption.appendChild(variantImage);
      variantOption.appendChild(label);
      variantGrid.appendChild(variantOption);
    });

    return variantGrid;
  }

  function calculateYarnPricing() {
    const selectedSubflow = flowState.flowConfig.subflows.find(sf => 
      sf.id === flowState.selectedSubflow ||
      (typeof flowState.selectedSubflow === 'number' && flowState.flowConfig.subflows.indexOf(sf) === flowState.selectedSubflow)
    );
    const selectedSize = selectedSubflow?.sizes.find(size => (size.id || selectedSubflow.sizes.indexOf(size)) === flowState.selectedSize);
    if (!selectedSubflow) {
      console.error('DEBUG: Selected subflow not found in calculateYarnPricing', {
        selectedSubflowId: flowState.selectedSubflow,
        availableSubflows: flowState.flowConfig.subflows.map(sf => ({ id: sf.id, name: sf.name }))
      });
      return;
    }
    if (!selectedSize) {
      console.error('DEBUG: Selected size not found in calculateYarnPricing', {
        selectedSizeId: flowState.selectedSize,
        selectedSubflowId: flowState.selectedSubflow,
        availableSizes: selectedSubflow?.sizes?.map(size => ({ id: size.id, name: size.name })) || []
      });
      return;
    }

    let yarnTotal = 0;
    selectedSize.products.forEach((product) => {
      // Skip unavailable products
      if (product.available === false) return;
      
      let productPrice = 0;
      if (product.isParentProduct) {
        // For parent products, use the selected variant's price
        const selectedVariantId = flowState.selectedVariants[product.productId];
        if (selectedVariantId && product.variants) {
          const selectedVariant = product.variants.find(v => v.variantId === selectedVariantId);
          if (selectedVariant && typeof selectedVariant.price === 'number') {
            productPrice = selectedVariant.price;
          }
        }
      } else {
        // For regular products, use the product's own price
        if (typeof product.price === 'number') {
          productPrice = product.price;
        }
      }
      yarnTotal += productPrice * product.quantity;
    });

    flowState.pricing.yarns = yarnTotal;
    updateNavigationButtonState();
    updateCartButtonPrice();
  }

  function renderAddOns(container, isAllInOne = false, allInOneSections = null) {
    console.log('DEBUG: Rendering add-ons');
    
    // Check if addons exist and have available items
    const hasAddOns = flowState.flowConfig.addOns && 
                      flowState.flowConfig.addOns.length > 0 &&
                      flowState.flowConfig.addOns.some(addOn => addOn.available !== false);
    
    if (!hasAddOns) {
      console.log('DEBUG: No addons available, hiding section');
      container.innerHTML = '';
      container.style.display = 'none';
      // Reset addon pricing when no addons
      flowState.pricing.addons = 0;
      updateCartButtonPrice();
      return;
    }

    container.innerHTML = '';
    container.style.display = 'block';
    container.classList.add('flow-app-step-content');

    const heading = document.createElement('h3');
    heading.className = 'flow-app-heading';
    heading.textContent = 'Select addons (optional)';
    container.appendChild(heading);

    const description = document.createElement('p');
    description.className = 'flow-app-description';
    description.textContent = 'Need anything else?';
    container.appendChild(description);

    if (flowState.flowConfig.addOns && flowState.flowConfig.addOns.length > 0) {
      const addOnsSection = document.createElement('div');
      addOnsSection.className = 'flow-app-addon-items';

      // Initialize mandatory add-ons
      flowState.flowConfig.addOns.forEach(addOn => {
        if (addOn.isMandatory) {
          flowState.selectedAddOns[addOn.id || addOn.productId] = true;
        }
      });

      flowState.flowConfig.addOns.forEach((addOn, index) => {
        // Skip unavailable add-ons
        if (addOn.available === false) return;
        
        const addOnItem = createAddOnItem(addOn, index, isAllInOne, allInOneSections);
        addOnsSection.appendChild(addOnItem);
      });

      container.appendChild(addOnsSection);
    }

    // Calculate addon pricing on load
    calculateAddonPricing();

    if (!isAllInOne) {
      // Navigation - show current total
      const totalPrice = flowState.pricing.basePattern + flowState.pricing.yarns + flowState.pricing.addons;
      const navigation = createNavigation(true, true, formatMoney(totalPrice));
      container.appendChild(navigation);
    }
  }

  function createAddOnItem(addOn, index, isAllInOne = false, allInOneSections = null) {
    const addOnItem = document.createElement('div');
    addOnItem.className = 'flow-app-addon-item';
    addOnItem.dataset.variantId = addOn.variantId;
    addOnItem.dataset.productId = addOn.productId;

    const content = document.createElement('div');
    content.className = 'flow-app-addon-content';

    const details = document.createElement('div');
    details.className = 'flow-app-addon-details';
    
    const title = document.createElement('div');
    title.className = 'flow-app-addon-title';
    title.textContent = addOn.productTitle;
    details.appendChild(title);

    if (addOn.variantTitle && addOn.variantTitle !== 'Default Title') {
      const variant = document.createElement('div');
      variant.className = 'flow-app-addon-variant';
      variant.textContent = addOn.variantTitle;
      details.appendChild(variant);
    }

    const price = document.createElement('div');
    price.className = 'flow-app-addon-price';
    price.textContent = typeof addOn.price === 'number' ? formatMoney(addOn.price) : formatMoney(0);

    content.appendChild(details);
    content.appendChild(price);

    // Selection logic: toggle selected state on click
    addOnItem.addEventListener('click', () => {
      const id = addOn.id || addOn.productId;
      flowState.selectedAddOns[id] = !flowState.selectedAddOns[id];
      // Update visual state
      if (flowState.selectedAddOns[id]) {
        addOnItem.classList.add('selected');
      } else {
        addOnItem.classList.remove('selected');
      }
      // Update pricing and display
      calculateAddonPricing();
      
      // Re-render review section in all-in-one mode
      if (isAllInOne && allInOneSections && allInOneSections.reviewSection) {
        renderOrderReview(allInOneSections.reviewSection, true);
      }
    });

    // Initialize state
    if (flowState.selectedAddOns[addOn.id || addOn.productId]) {
      addOnItem.classList.add('selected');
    }

    addOnItem.appendChild(content);

    return addOnItem;
  }

  function calculateAddonPricing() {
    let addonTotal = 0;
    Object.keys(flowState.selectedAddOns).forEach(addonId => {
      if (flowState.selectedAddOns[addonId]) {
        const addon = flowState.flowConfig.addOns.find(a => (a.id || a.productId) === addonId);
        if (addon && addon.price) {
          addonTotal += parseFloat(addon.price);
        }
      }
    });
    flowState.pricing.addons = addonTotal;
    updateNavigationButtonPrice();
    updateCartButtonPrice();
  }

  function renderOrderReview(container, isAllInOne = false) {
    console.log('DEBUG: Rendering order review');
    
    container.innerHTML = '';
    container.classList.add('flow-app-step-content');

    const heading = document.createElement('h3');
    heading.className = 'flow-app-heading';
    heading.textContent = 'Review your selections';
    container.appendChild(heading);

    const description = document.createElement('p');
    description.className = 'flow-app-description';
    description.textContent = 'Review your selections before adding to cart';
    container.appendChild(description);

    const reviewSection = document.createElement('div');
    reviewSection.className = 'flow-app-review-section';

    // --- Update total before rendering ---
    flowState.pricing.total = (
      (flowState.pricing.basePattern || 0) +
      (flowState.pricing.yarns || 0) +
      (flowState.pricing.addons || 0)
    );

    // Update cart button price
    updateCartButtonPrice();

    // Pattern section
    const patternCategory = document.createElement('div');
    patternCategory.className = 'flow-app-review-category';
    
    const patternTitle = document.createElement('h4');
    patternTitle.className = 'flow-app-review-category-title';
    patternTitle.textContent = 'Pattern';
    patternCategory.appendChild(patternTitle);

    // --- Always use base product name, never subflow name ---
    const patternItem = document.createElement('div');
    patternItem.className = 'flow-app-review-item';
    
    const patternDetails = document.createElement('div');
    patternDetails.className = 'flow-app-review-item-details';
    
    const patternName = document.createElement('div');
    patternName.className = 'flow-app-review-item-name';
    let basePatternName =
      (flowState.flowConfig && flowState.flowConfig.baseProduct && flowState.flowConfig.baseProduct.title) ||
      (flowState.flowConfig && flowState.flowConfig.productTitle) ||
      (document.getElementById('flow-app-container')?.dataset.productTitle) ||
      'Pattern';
    patternName.textContent = basePatternName;
    patternDetails.appendChild(patternName);

    const patternPrice = document.createElement('div');
    patternPrice.className = 'flow-app-review-item-price';
    patternPrice.textContent = formatMoney(flowState.pricing.basePattern);

    patternItem.appendChild(patternDetails);
    patternItem.appendChild(patternPrice);
    patternCategory.appendChild(patternItem);

    reviewSection.appendChild(patternCategory);

    // Yarn section
    const selectedSubflow = flowState.flowConfig.subflows.find(sf => sf.id === flowState.selectedSubflow || sf === flowState.selectedSubflow);
    const selectedSize = selectedSubflow?.sizes.find(size => (size.id || selectedSubflow.sizes.indexOf(size)) === flowState.selectedSize);
    if (selectedSize && selectedSize.products) {
      const yarnCategory = document.createElement('div');
      yarnCategory.className = 'flow-app-review-category';
      
      const yarnTitle = document.createElement('h4');
      yarnTitle.className = 'flow-app-review-category-title';
      yarnTitle.textContent = 'Yarn';
      yarnCategory.appendChild(yarnTitle);

      selectedSize.products.forEach(product => {
        // Skip unavailable products
        if (product.available === false) return;
        
        const yarnItem = document.createElement('div');
        yarnItem.className = 'flow-app-review-item';

        const yarnDetails = document.createElement('div');
        yarnDetails.className = 'flow-app-review-item-details';
        
        const yarnName = document.createElement('div');
        yarnName.className = 'flow-app-review-item-name';
        yarnName.textContent = product.productTitle;
        yarnDetails.appendChild(yarnName);

        let variantText = '';
        let price = product.price;
        
        if (product.isParentProduct && flowState.selectedVariants[product.productId]) {
          const selectedVariant = product.variants?.find(v => v.variantId === flowState.selectedVariants[product.productId]);
          if (selectedVariant) {
            variantText = selectedVariant.variantTitle;
            if (selectedVariant.price !== undefined) {
              price = selectedVariant.price;
            }
          }
        } else if (!product.isParentProduct && product.variantTitle && product.variantTitle !== 'Default Title') {
          variantText = product.variantTitle;
        }

        if (variantText) {
          const yarnVariant = document.createElement('div');
          yarnVariant.className = 'flow-app-review-item-variant';
          yarnVariant.textContent = `${variantText} • ${product.quantity}pcs`;
          yarnDetails.appendChild(yarnVariant);
        }

        const yarnPrice = document.createElement('div');
        yarnPrice.className = 'flow-app-review-item-price';
        const totalPrice = product.quantity * parseFloat(price || 0);
        yarnPrice.textContent = formatMoney(totalPrice);

        yarnItem.appendChild(yarnDetails);
        yarnItem.appendChild(yarnPrice);
        yarnCategory.appendChild(yarnItem);
      });

      reviewSection.appendChild(yarnCategory);
    }

    // Addons section
    const selectedAddOnIds = Object.keys(flowState.selectedAddOns).filter(id => flowState.selectedAddOns[id]);
    console.log('DEBUG: Selected add-ons:', selectedAddOnIds, flowState.selectedAddOns);
    if (selectedAddOnIds.length > 0) {
      const addonCategory = document.createElement('div');
      addonCategory.className = 'flow-app-review-category';
      
      const addonTitle = document.createElement('h4');
      addonTitle.className = 'flow-app-review-category-title';
      addonTitle.textContent = 'Addons';
      addonCategory.appendChild(addonTitle);

      selectedAddOnIds.forEach(addonId => {
        const addon = flowState.flowConfig.addOns.find(a => (a.id || a.productId) === addonId);
        if (addon) {
          const addonItem = document.createElement('div');
          addonItem.className = 'flow-app-review-item';
          
          const addonDetails = document.createElement('div');
          addonDetails.className = 'flow-app-review-item-details';
          
          const addonName = document.createElement('div');
          addonName.className = 'flow-app-review-item-name';
          addonName.textContent = addon.productTitle;
          addonDetails.appendChild(addonName);

          if (addon.variantTitle && addon.variantTitle !== 'Default Title') {
            const addonVariant = document.createElement('div');
            addonVariant.className = 'flow-app-review-item-variant';
            addonVariant.textContent = addon.variantTitle;
            addonDetails.appendChild(addonVariant);
          }

          const addonPrice = document.createElement('div');
          addonPrice.className = 'flow-app-review-item-price';
          addonPrice.textContent = typeof addon.price === 'number' ? formatMoney(addon.price) : formatMoney(0);

          addonItem.appendChild(addonDetails);
          addonItem.appendChild(addonPrice);
          addonCategory.appendChild(addonItem);
        }
      });

      reviewSection.appendChild(addonCategory);
    }

    container.appendChild(reviewSection);

    if (!isAllInOne) {
      // --- Use up-to-date total for Add to Cart button ---
      const navigation = createFinalNavigation();
      container.appendChild(navigation);
    } else {
      // In all-in-one mode, always show the Add to Cart button at the bottom
      const navigation = createFinalNavigation();
      container.appendChild(navigation);
    }
  }

  function updateNavigationButtonState() {
    const continueButton = document.querySelector('.flow-app-next-button');
    if (!continueButton) return;

    let canContinue = false;

    switch (flowState.currentStep) {
      case 1:
        canContinue = flowState.selectedSubflow !== null;
        break;
      case 2:
        canContinue = flowState.selectedSize !== null;
        break;
      case 3:
      case 4:
      case 5:
        canContinue = true;
        break;
    }

    continueButton.disabled = !canContinue;
  }

  function createNavigation(showBack, canContinue, buttonPrice = null) {
    const navigation = document.createElement('div');
    navigation.className = 'flow-app-navigation';

    if (showBack) {
      const backButton = document.createElement('button');
      backButton.className = 'flow-app-back-button';
      backButton.textContent = '←';
      backButton.addEventListener('click', () => {
        if (flowState.currentStep === 1) {
          // Go back to activation button (step 0)
          const container = document.getElementById('flow-app-container');
          renderActivationButton(container);
        } else if (flowState.currentStep > 1) {
          flowState.currentStep--;
          renderCurrentStep();
        }
      });
      navigation.appendChild(backButton);
    }

    const continueButton = document.createElement('button');
    continueButton.className = 'flow-app-next-button';
    continueButton.disabled = !canContinue;
    
    // Set button text to always be 'Continue • £XX.XX' if price is present
    let buttonText = 'Continue';
    if (buttonPrice) {
      continueButton.innerHTML = `<span>${buttonText} <span style='margin: 0 6px;'>•</span> ${buttonPrice}</span>`;
    } else {
      continueButton.textContent = buttonText;
    }
    
    continueButton.addEventListener('click', () => {
      if (flowState.currentStep < flowState.totalSteps && !continueButton.disabled) {
        flowState.currentStep++;
        renderCurrentStep();
      }
    });

    navigation.appendChild(continueButton);

    return navigation;
  }

  function createFinalNavigation() {
    const navigation = document.createElement('div');
    navigation.className = 'flow-app-navigation';

    const cartButton = document.createElement('button');
    cartButton.className = 'flow-app-cart-button';
    // Initial placeholder - updateCartButtonPrice() will set the correct price
    cartButton.innerHTML = `<span>Add to Cart</span><span class="flow-app-button-price">${formatMoney(0)}</span>`;
    cartButton.addEventListener('click', () => addToCart());

    navigation.appendChild(cartButton);

    // Update price after button is created
    setTimeout(() => updateCartButtonPrice(), 0);

    return navigation;
  }

  async function validateWithBackendAPI() {
    console.log('📊 Validating inventory with backend API...');
    
    // Get flowId from config
    const flowId = flowState.flowConfig.id;
    if (!flowId) {
      throw new Error('Flow ID not found in configuration');
    }
    
    // Convert selectedAddOns to variant IDs array
    const selectedAddOnVariants = [];
    Object.keys(flowState.selectedAddOns).forEach(addonId => {
      if (flowState.selectedAddOns[addonId]) {
        const addon = flowState.flowConfig.addOns.find(a => (a.id || a.productId) === addonId);
        if (addon && addon.variantId) {
          selectedAddOnVariants.push(addon.variantId);
        }
      }
    });
    
    const requestData = {
      flowId: flowId,
      subflowId: flowState.selectedSubflow,
      sizeId: flowState.selectedSize,
      selectedVariants: flowState.selectedVariants,
      selectedAddOnVariants: selectedAddOnVariants
    };
    
    console.log('📤 Sending validation request:', requestData);
    
    const response = await fetch('https://hparptkwdymlqycbddyc.supabase.co/functions/v1/validate-flow-inventory', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhwYXJwdGt3ZHltbHF5Y2JkZHljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI4ODg1ODQsImV4cCI6MjA1ODQ2NDU4NH0.XCOvSdTTakj_snmbCVPpFJBlo9YOGtFZMPNbcSK3ORw'
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Inventory validation failed');
    }
    
    const validationResult = await response.json();
    console.log('📥 Validation response:', validationResult);
    
    if (!validationResult.valid) {
      // Create detailed error message from backend response
      const errorDetails = validationResult.errors.map(error => 
        `${error.productTitle}: ${error.message}`
      ).join('\n');
      
      throw new Error(`Inventory validation failed:\n${errorDetails}`);
    }
    
    console.log(`✅ Inventory validation passed - ${validationResult.totalItems} items validated`);
    return true;
  }

  async function addToCart() {
    // Get cart button reference at the top for proper scoping
    const cartButton = document.querySelector('.flow-app-cart-button');
    if (!cartButton) {
      console.error('Cart button not found');
      return;
    }

    try {
      console.log('🛒 Starting add to cart process...');
      
      // Validate inventory before adding to cart
      console.log('📊 Running backend inventory validation...');
      await validateWithBackendAPI();
      console.log('✅ Inventory validation passed - proceeding with cart add');
      
      const cartItems = [];
      
      // Get base variant ID
      const baseVariantId = getSelectedBaseVariantId();
      if (baseVariantId) {
        cartItems.push({
          id: baseVariantId,
          quantity: 1
        });
      }

      // Add selected size products
      const selectedSubflow = flowState.flowConfig.subflows.find(sf => sf.id === flowState.selectedSubflow || sf === flowState.selectedSubflow);
      const selectedSize = selectedSubflow?.sizes.find(size => (size.id || selectedSubflow.sizes.indexOf(size)) === flowState.selectedSize);
      
      if (selectedSize && selectedSize.products) {
        selectedSize.products.forEach(product => {
          if (product.available === false) return;
          
          if (product.isParentProduct && product.variants) {
            const selectedVariantId = flowState.selectedVariants[product.productId];
            if (selectedVariantId) {
              const variant = product.variants.find(v => v.variantId === selectedVariantId);
              if (variant && variant.available !== false) {
                cartItems.push({
                  id: selectedVariantId,
                  quantity: product.quantity
                });
              }
            }
          } else {
            cartItems.push({
              id: product.variantId,
              quantity: product.quantity
            });
          }
        });
      }

      // Add selected add-ons
      Object.keys(flowState.selectedAddOns).forEach(addonId => {
        if (flowState.selectedAddOns[addonId]) {
          const addon = flowState.flowConfig.addOns.find(a => (a.id || a.productId) === addonId);
          if (addon && addon.available !== false) {
            cartItems.push({
              id: addon.variantId,
              quantity: 1
            });
          }
        }
      });

      if (cartItems.length === 0) {
        alert('Cannot add to cart: all selected products are out of stock.');
        return;
      }

      console.log('🛍️ Cart items to add:', cartItems);

      // Show loading state
      const originalHTML = cartButton.innerHTML;
      cartButton.disabled = true;
      cartButton.innerHTML = '<span>Adding to Cart...</span>';

      // Add to cart using Shopify AJAX API
      console.log('📡 Sending cart add request to Shopify...');
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: cartItems }),
      });

      console.log('📥 Shopify response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      // Parse response body to check for partial additions
      const responseData = await response.json();
      console.log('📄 Shopify response data:', responseData);

      // Check if response indicates partial success (fewer items added than requested)
      if (responseData.message && (
          responseData.message.includes('Only') && responseData.message.includes('items were added') ||
          responseData.message.includes('due to availability') ||
          responseData.message.includes('not enough inventory')
        )) {
        // This is a partial addition - treat as error and remove added items
        throw new Error(responseData.message);
      }

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to add items to cart');
      }

      // After successful response, verify all requested quantities were added
      console.log('🔍 Verifying actual cart quantities...');
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();

      // Check if all requested quantities were actually added
      let totalExpected = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      let totalAdded = 0;
      let addedItems = [];

      cartItems.forEach(item => {
        const cartItem = currentCart.items.find(ci => ci.variant_id == item.id);
        if (cartItem) {
          totalAdded += cartItem.quantity;
          addedItems.push({
            variantId: item.id,
            expected: item.quantity,
            actual: cartItem.quantity
          });
        } else {
          addedItems.push({
            variantId: item.id,
            expected: item.quantity,
            actual: 0
          });
        }
      });

      console.log('📊 Quantity verification:', {
        totalExpected,
        totalAdded,
        itemDetails: addedItems
      });

      if (totalAdded < totalExpected) {
        console.warn('❌ Partial addition detected - cleaning up cart...');
        
        // Remove all items that were just added
        const removePromises = cartItems.map(item => 
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.id, quantity: 0 })
          })
        );
        
        await Promise.all(removePromises);
        console.log('🧹 Cleanup complete - refreshing cart UI...');

        // Force cart UI refresh - multiple methods for different themes
        try {
          // Method 1: Trigger cart drawer refresh if it exists
          if (window.theme && window.theme.cart && window.theme.cart.refresh) {
            window.theme.cart.refresh();
          }
          
          // Method 2: Dispatch cart update events
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          window.dispatchEvent(new CustomEvent('cartUpdated'));
          
          // Method 3: Update cart count if element exists
          const cartCount = document.querySelector('[data-cart-count], .cart-count, #cart-count');
          if (cartCount) {
            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();
            cartCount.textContent = cartData.item_count;
          }
          
          console.log('🔄 Cart UI refresh attempted');
        } catch (refreshError) {
          console.warn('Could not refresh cart UI:', refreshError);
        }

        throw new Error('Some items in your selection are out of stock or don\'t have enough inventory. Please check back later or select a different color.');
      }

      // Show success and redirect
      cartButton.innerHTML = '<span>Added to Cart!</span>';
      
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Check if it's a Shopify inventory or stock error
      let errorMessage = 'There was an error adding items to your cart. Please try again.';
      
      if (error.message.includes('inventory') || error.message.includes('stock') || 
          error.message.includes('out of stock') || error.message.includes('sold out') ||
          error.message.includes('availability') || error.message.includes('added to your cart due to') ||
          (error.message.includes('Only') && error.message.includes('items were added')) ||
          error.message.includes('Inventory validation failed') ||
          error.message.includes('Insufficient stock') ||
          error.message.includes('Please select a variant')) {
        errorMessage = 'Some items in your selection are out of stock or don\'t have enough inventory. Please check back later or select a different color.';
      }
      
      alert(errorMessage);
      
      // Restore button state on error
      cartButton.disabled = false;
      cartButton.innerHTML = originalHTML;
      
      // Reset the flow to starting point instead of just re-enabling button
      const container = document.getElementById('flow-app-container');
      if (container) {
        // Reset flow state but keep configuration
        const originalConfig = flowState.flowConfig;
        flowState = {
          currentStep: 1,
          totalSteps: 5,
          selectedSubflow: null,
          selectedSize: null,
          selectedVariants: {},
          selectedAddOns: {},
          flowConfig: originalConfig,
          pricing: {
            basePattern: 0,
            yarns: 0,
            addons: 0,
            total: 0
          }
        };
        
        // Set defaults again
        setDefaultSelections();
        
        // Re-render the all-in-one view (fresh start)
        renderAllInOne(container);
      }
    }
  }

  function getSelectedBaseVariantId() {
    try {
      // Method 1: Check product form
      const productForm = document.querySelector('form[action="/cart/add"]');
      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          return variantInput.value;
        }
        
        const variantSelect = productForm.querySelector('select[name="id"]');
        if (variantSelect && variantSelect.value) {
          return variantSelect.value;
        }
      }
      
      // Method 2: Check data attributes
      const variantIdElement = document.querySelector('[data-product-select], [data-variant-id]');
      if (variantIdElement) {
        return variantIdElement.value || variantIdElement.dataset.variantId;
      }
      
      // Method 3: Check JSON scripts
      const jsonScripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of jsonScripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (data && data.selected_variant_id) {
            return data.selected_variant_id;
          }
          if (data && data.selected_or_first_available_variant && data.selected_or_first_available_variant.id) {
            return data.selected_or_first_available_variant.id;
          }
        } catch (e) {
          // Continue to next script
        }
      }
      
      // Method 4: Check window.meta
      if (window.meta && window.meta.product && window.meta.product.selected_variant) {
        return window.meta.product.selected_variant.id;
      }
      
      // Method 5: Fallback to container data (Liquid template provides variant ID)
      const container = document.getElementById('flow-app-container');
      if (container && container.dataset.variantId) {
        console.log('Using variant ID from Liquid template:', container.dataset.variantId);
        return container.dataset.variantId;
      }
      
      return null;
    } catch (error) {
      console.error('Error finding base variant ID:', error);
      return null;
    }
  }

  function updateNavigationButtonPrice() {
    const continueButton = document.querySelector('.flow-app-next-button');
    if (!continueButton) return;
    let price = 0;
    switch (flowState.currentStep) {
      case 1:
        price = flowState.pricing.basePattern;
        break;
      case 2:
        price = flowState.pricing.basePattern;
        break;
      case 3:
        price = flowState.pricing.basePattern + flowState.pricing.yarns;
        break;
      case 4:
        price = flowState.pricing.basePattern + flowState.pricing.yarns + flowState.pricing.addons;
        break;
      default:
        price = flowState.pricing.basePattern + flowState.pricing.yarns + flowState.pricing.addons;
    }
    continueButton.innerHTML = `<span>Continue <span style='margin: 0 6px;'>•</span> ${formatMoney(price)}</span>`;
  }

  function updateCartButtonPrice() {
    const cartButton = document.querySelector('.flow-app-cart-button');
    if (!cartButton) return;
    
    // Use flowState pricing which should be more reliable
    const totalPrice = (
      (flowState.pricing.basePattern || 0) +
      (flowState.pricing.yarns || 0) +
      (flowState.pricing.addons || 0)
    );
    
    console.log('DEBUG: Cart button price update:', {
      basePattern: flowState.pricing.basePattern,
      yarns: flowState.pricing.yarns,
      addons: flowState.pricing.addons,
      total: totalPrice
    });
    
    cartButton.innerHTML = `<span>Add to Cart</span><span class="flow-app-button-price">${formatMoney(totalPrice)}</span>`;
  }

  // ---- place Flow button under Shopify "Add to cart"
  function getATCButton() {
    return (
      document.querySelector('product-form button[type="submit"][name="add"]') ||
      document.querySelector('form[action^="/cart/add"] button[type="submit"]') ||
      document.querySelector('button[name="add"]')
    );
  }
  
  function placeFlowUnderATC() {
    const container = document.getElementById('flow-app-container');
    const atc = getATCButton();
    if (!container || !atc) return;

    const after =
      atc.closest('.product-form__buttons, product-form, .product-form, form') ||
      atc.parentElement;

    if (after && after.nextElementSibling !== container) {
      after.insertAdjacentElement('afterend', container);
    }
  }
  
  // run on load and keep it in place if the theme re-renders
  function keepPlaced() {
    placeFlowUnderATC();
    const mo = new MutationObserver(() => placeFlowUnderATC());
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();